import type { ComponentProps } from 'react'
import {
  FieldInfo,
  hasFieldError,
  readOnlyTriggerClass,
  useFieldContext,
  useFormReadOnly,
  useIsFieldOptional,
} from './shared'
import { MultiCombobox } from '@/components/custom/multi-combobox'
import { FieldLabelRow } from './field-label-row'
import { cn } from '@/lib/utils'
import { HORIZONTAL_CONTROL_CLASS, HORIZONTAL_LABEL_CLASS, HORIZONTAL_ROW_CLASS } from './horizontal-field'

export default function MultiComboboxNumberField(
  props: ComponentProps<'select'> & {
    label: string
    search: string
    empty: string
    options: { value: number; label: string }[]
    creatable?: boolean
    orientation?: 'vertical' | 'horizontal'
    optional?: boolean
  },
) {
  const field = useFieldContext<number[]>()
  const formReadOnly = useFormReadOnly()
  const isOptional = useIsFieldOptional(field.name, props.optional)
  const isHorizontal = props.orientation === 'horizontal'
  const isEmpty = !field.state.value?.length

  return (
    <div className={cn(props.className, formReadOnly && readOnlyTriggerClass)}>
      <div className={isHorizontal ? HORIZONTAL_ROW_CLASS : 'grid gap-2'}>
        {props.label && (
          <FieldLabelRow
            label={props.label}
            htmlFor={field.name}
            hasError={field.state.meta.isTouched && !field.state.meta.isValid}
            optional={isOptional}
            compactOptional={isHorizontal}
            className={isHorizontal ? HORIZONTAL_LABEL_CLASS : undefined}
          />
        )}
        <div className={isHorizontal ? HORIZONTAL_CONTROL_CLASS : undefined}>
          {formReadOnly && isEmpty ? (
            <div className="text-muted-foreground flex h-8 items-center">-</div>
          ) : (
            <MultiCombobox
              id={field.name}
              placeholder={props.label}
              commandEmpty={props.empty}
              searchPlaceholder={props.search}
              creatable={!formReadOnly && props.creatable}
              isError={hasFieldError(field.state.meta)}
              options={props.options.map((option) => ({
                value: option.value.toString(),
                label: option.label,
              }))}
              value={field.state.value?.map((value) => value.toString()) ?? []}
              onChange={(value) => field.handleChange(value.map(Number))}
            />
          )}
          <FieldInfo fieldState={field.state} />
        </div>
      </div>
    </div>
  )
}
